import { useAuthStore } from '@/app/store/auth.store';

import type { MenuCategoryKey, PermissionValue } from '../types/role.types';

const RANK: Record<PermissionValue, number> = {
  NO_PERMISSION: 0,
  READ: 1,
  WRITE: 2,
};

// Effective permission for the current admin = the level the backend's GET /me returned for the
// category (held in the auth store). Root short-circuits to WRITE everywhere. No FE heuristic —
// the per-user permission map is server-owned.
function levelFor(
  permissions: Record<string, string>,
  isRoot: boolean,
  category: MenuCategoryKey,
): PermissionValue {
  if (isRoot) return 'WRITE';
  const level = permissions[category];
  return level === 'WRITE' || level === 'READ' ? level : 'NO_PERMISSION';
}

export function useCan(category: MenuCategoryKey, requiredLevel: PermissionValue): boolean {
  const permissions = useAuthStore((s) => s.permissions);
  const isRoot = useAuthStore((s) => s.isRoot);
  return RANK[levelFor(permissions, isRoot, category)] >= RANK[requiredLevel];
}

// Returns a permission-check function bound to the current user. Useful when
// you need to check several (category, level) pairs in one render (e.g. filtering
// navigation items) without violating the rules of hooks.
export function usePermissionChecker(): (
  category: MenuCategoryKey,
  level: PermissionValue,
) => boolean {
  const permissions = useAuthStore((s) => s.permissions);
  const isRoot = useAuthStore((s) => s.isRoot);
  return (category, level) =>
    RANK[levelFor(permissions, isRoot, category)] >= RANK[level];
}

// Returns the role names currently held by the logged-in admin.
export function useHeldRoleNames(): readonly string[] {
  return useAuthStore((s) => s.roles);
}
